import { z } from "zod";

export enum PaymentProvider {
	Shift4 = "Shift4",
	None = "None",
	"MX-Merchant" = "MX-Merchant",
	"PCI-Booking" = "PCI-Booking",
	Coinflow = "Coinflow",
	Adyen = "Adyen",
}

// TODO check to update this enum with new cards
export enum AcceptedCards {
	"American Express" = "American Express",
	BC = "BC",
	Dankort = "Dankort",
	Delta = "Delta",
	"Diner's Club" = "Diner's Club",
	Discover = "Discover",
	Electron = "Electron",
	Elo = "Elo",
	enRoute = "enRoute",
	"Euro Card" = "Euro Card",
	Hipercard = "Hipercard",
	JCB = "JCB",
	Maestro = "Maestro",
	MasterCard = "MasterCard",
	Solo = "Solo",
	Switch = "Switch",
	"Union Pay" = "Union Pay",
	Visa = "Visa",
}

export const PaymentProviderOptions = [
	{ value: PaymentProvider.Shift4, label: "Shift4" },
	{ value: PaymentProvider["MX-Merchant"], label: "MX-Merchant" },
	{ value: PaymentProvider.None, label: "None" },
	{ value: PaymentProvider["PCI-Booking"], label: "PCI-Booking" },
	{ value: PaymentProvider.Coinflow, label: "Coinflow" },
	{ value: PaymentProvider.Adyen, label: "Adyen" },
];

export interface PaymentGateway {
	Name: string;
	DisplayName: string;
}

export function buildPaymentConnectionFormSchema(skipApiKey = false) {
	return z
		.object({
			provider: z.enum(PaymentProvider),
			// Plain strings on purpose: options come from the provider's
			// acceptedCards (API), so a hardcoded enum here only drifts —
			// e.g. dev DB "Diners Club" vs enum "Diner's Club" silently
			// blocked submit with an index-level error (rendered "undefined").
			supportedCards: z.array(z.string().min(1)),
			// Derived from the resource's formatting.currency, never user-picked:
			// it denominates every charge made with this payment method.
			currency: z.string().min(1, "val.required"),
			apiKey: z.string().optional(),
			secretKey: z.string().optional(),
			externalId: z.string().optional(),
			clientKey: z.string().optional(),
			hmacKey: z.string().optional(),
			groupId: z.string().optional(),
		})
		.check((ctx) => {
			const { value, issues } = ctx;
			if (value.provider === PaymentProvider.None) return;

			const isAdyen = value.provider === PaymentProvider.Adyen;

			// skipApiKey models the MX-Merchant group flow (platform credentials,
			// nothing to type). Adyen is bring-your-own-account: the credentials
			// are per resource even for groups, so it never skips.
			if (
				value.provider !== PaymentProvider.Coinflow &&
				(!skipApiKey || isAdyen) &&
				(!value.apiKey || value.apiKey.trim().length === 0)
			) {
				issues.push({
					code: "custom",
					path: ["apiKey"],
					message: "val.required",
					input: value,
				});
			}

			if (isAdyen) {
				if (!value.clientKey || value.clientKey.trim().length === 0) {
					issues.push({
						code: "custom",
						path: ["clientKey"],
						message: "val.required",
						input: value,
					});
				}
				// merchantAccount — travels as externalId, same as Coinflow's
				// sub-merchant. hmacKey stays optional (webhook reconciliation only).
				if (!value.externalId || value.externalId.trim().length === 0) {
					issues.push({
						code: "custom",
						path: ["externalId"],
						message: "val.required",
						input: value,
					});
				}
			}

			if (
				value.provider === PaymentProvider.Coinflow &&
				(!value.externalId || value.externalId.trim().length === 0)
			) {
				issues.push({
					code: "custom",
					path: ["externalId"],
					message: "val.required",
					input: value,
				});
			}

			if (!skipApiKey && value.provider === PaymentProvider["MX-Merchant"]) {
				if (!value.secretKey || value.secretKey.trim().length === 0) {
					issues.push({
						code: "custom",
						path: ["secretKey"],
						message: "val.required",
						input: value,
					});
				}
				if (!value.externalId || value.externalId.trim().length === 0) {
					issues.push({
						code: "custom",
						path: ["externalId"],
						message: "val.required",
						input: value,
					});
				}
			}

			if (!value.supportedCards || value.supportedCards.length === 0) {
				issues.push({
					code: "custom",
					path: ["supportedCards"],
					message: "val.required",
					input: value,
				});
			}
		});
}

export const paymentConnectionFormSchema = buildPaymentConnectionFormSchema();

export type PaymentConnectionFormValues = z.infer<
	typeof paymentConnectionFormSchema
>;

// ── Dynamic schema for PCI-Booking provider ───────────────────────────────────
export type DynamicFormValues = {
	provider: string;
	currency: string;
	credentialId: string;
	pciGateway: string; // the selected PCI Booking gateway name
	credentials: Record<string, unknown>;
	supportedCards: string[];
};

export function buildDynamicSchema(fields: string[]) {
	const credentialsShape: Record<string, z.ZodTypeAny> = {};
	for (const fieldName of fields) {
		credentialsShape[fieldName] = z.string().min(1, "val.required");
	}

	return z.object({
		provider: z.string().min(1, "val.required"),
		currency: z.string().min(1, "val.required"),
		pciGateway: z.string().min(1, "val.required"),
		credentialId: z.string().min(1, "val.required"),
		credentials: z.object(credentialsShape),
		// Same rationale as buildPaymentConnectionFormSchema: provider's
		// acceptedCards is the source of truth, not the local enum.
		supportedCards: z.array(z.string().min(1)).min(1, "val.required"),
	});
}

export type ConnectedProvider = {
	provider: PaymentProvider;
	currency: string;
	id: string;
	supportedCards: AcceptedCards[];
};

export type Provider = {
	id: string;
	name: string;
	acceptedCards: string[];
};
